// ============================================================================
// POKER GLOSSARY - beginner-first reference for every training section
// ============================================================================
// Surfaces the existing POKER_GLOSSARY (125+ terms authored in
// trainingAnalysisService) as a searchable, filterable reference. Beginner
// mode shows the simple definitions first; every entry keeps its example,
// common mistake, and pro tip.
// ============================================================================
import React, { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box, Container, Heading, Text, VStack, HStack, Badge, Input, InputGroup,
  InputLeftElement, Wrap, WrapItem, Switch,
} from '@chakra-ui/react';
import { ArrowLeft, Search, BookOpen, Lightbulb, AlertTriangle, Grid3x3 } from 'lucide-react';
import { POKER_COLORS } from '../theme/designSystem';
import PokerCard from '../components/ui/PokerCard';
import PokerButton from '../components/ui/PokerButton';
import { POKER_GLOSSARY, GlossaryEntry } from '../services/training/trainingAnalysisService';

const CATEGORY_LABELS: Record<GlossaryEntry['category'], string> = {
  'basics': 'Basics',
  'actions': 'Actions',
  'structure': 'Structure',
  'hand-rankings': 'Hand Rankings',
  'positions': 'Positions',
  'streets': 'Streets',
  'strategy': 'Strategy',
  'stats': 'Stats',
  'player-types': 'Player Types',
  'tournament': 'Tournament',
};

const LEVEL_COLORS: Record<GlossaryEntry['level'], string> = {
  'absolute-beginner': 'green',
  'beginner': 'teal',
  'intermediate': 'yellow',
  'advanced': 'purple',
};

const GlossaryPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [search, setSearch] = useState('');
  // Deep-linkable: /training/glossary?category=tournament opens pre-filtered
  // (used by the Tournament Training hub's Glossary button).
  const initialCategory = (searchParams.get('category') as GlossaryEntry['category'] | null);
  const [category, setCategory] = useState<GlossaryEntry['category'] | 'all'>(
    initialCategory && CATEGORY_LABELS[initialCategory] ? initialCategory : 'all'
  );
  const [simpleMode, setSimpleMode] = useState(true);

  const entries = useMemo(() => {
    const all = Object.values(POKER_GLOSSARY);
    const s = search.trim().toLowerCase();
    return all
      .filter(e => category === 'all' || e.category === category)
      .filter(e => !s
        || e.term.toLowerCase().includes(s)
        || e.definition.toLowerCase().includes(s)
        || (e.simpleDefinition || '').toLowerCase().includes(s))
      .sort((a, b) => a.term.localeCompare(b.term));
  }, [search, category]);

  return (
    <Box minH="100vh" bg="gray.900" pb={16}>
      <Container maxW="4xl" pt={6}>
        <HStack mb={2}>
          <PokerButton size="sm" variant="secondary" onClick={() => navigate('/training')}>
            <ArrowLeft size={14} style={{ marginRight: 4 }} /> Training
          </PokerButton>
          <Box flex={1} />
          <PokerButton size="sm" variant="primary" onClick={() => navigate('/training/charts')}>
            <Grid3x3 size={14} style={{ marginRight: 4 }} /> Preflop charts
          </PokerButton>
        </HStack>

        <HStack mb={1}>
          <BookOpen size={24} color={POKER_COLORS.cyan} />
          <Heading size="lg" color="white">Poker Glossary</Heading>
        </HStack>
        <Text color="gray.400" fontSize="sm" mb={4}>
          Every term you'll meet in training, explained. New to poker? Keep
          Simple Mode on for plain-English definitions.
        </Text>

        <HStack mb={4} spacing={4} flexWrap="wrap">
          <InputGroup maxW="320px">
            <InputLeftElement pointerEvents="none">
              <Search size={16} color="#64748b" />
            </InputLeftElement>
            <Input
              value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search terms..." bg="gray.800" borderColor="gray.700"
              color="white" size="sm" borderRadius="md"
            />
          </InputGroup>
          <HStack>
            <Text fontSize="xs" color="gray.400">Simple Mode</Text>
            <Switch size="sm" colorScheme="cyan" isChecked={simpleMode}
              onChange={e => setSimpleMode(e.target.checked)} />
          </HStack>
        </HStack>

        <Wrap mb={5} spacing={2}>
          <WrapItem>
            <Badge
              cursor="pointer" px={3} py={1} borderRadius="full"
              colorScheme={category === 'all' ? 'cyan' : 'gray'}
              onClick={() => setCategory('all')}
            >All ({Object.keys(POKER_GLOSSARY).length})</Badge>
          </WrapItem>
          {(Object.keys(CATEGORY_LABELS) as Array<GlossaryEntry['category']>).map(cat => (
            <WrapItem key={cat}>
              <Badge
                cursor="pointer" px={3} py={1} borderRadius="full"
                colorScheme={category === cat ? 'cyan' : 'gray'}
                onClick={() => setCategory(cat)}
              >{CATEGORY_LABELS[cat]}</Badge>
            </WrapItem>
          ))}
        </Wrap>

        {entries.length === 0 ? (
          <Text color="gray.500" textAlign="center" py={10}>No terms match your search.</Text>
        ) : (
          <VStack align="stretch" spacing={3}>
            {entries.map(e => (
              <PokerCard key={e.term}>
                <VStack align="stretch" spacing={2} p={1}>
                  <HStack justify="space-between">
                    <Text color="white" fontWeight="bold" fontSize="md">{e.term}</Text>
                    <HStack>
                      <Badge colorScheme={LEVEL_COLORS[e.level]} fontSize="9px">
                        {e.level.replace('-', ' ')}
                      </Badge>
                      <Badge colorScheme="gray" fontSize="9px">{CATEGORY_LABELS[e.category]}</Badge>
                    </HStack>
                  </HStack>

                  <Text color="gray.200" fontSize="sm">
                    {simpleMode && e.simpleDefinition ? e.simpleDefinition : e.definition}
                  </Text>

                  {e.example && (
                    <Text color="gray.400" fontSize="xs" fontStyle="italic">
                      Example: {e.example}
                    </Text>
                  )}

                  {e.commonMistake && (
                    <HStack align="start" spacing={2}>
                      <AlertTriangle size={13} color="#fbbf24" style={{ marginTop: 2, flexShrink: 0 }} />
                      <Text color="yellow.200" fontSize="xs">{e.commonMistake}</Text>
                    </HStack>
                  )}

                  {e.proTip && (
                    <HStack align="start" spacing={2}>
                      <Lightbulb size={13} color="#22d3ee" style={{ marginTop: 2, flexShrink: 0 }} />
                      <Text color="cyan.200" fontSize="xs">{e.proTip}</Text>
                    </HStack>
                  )}

                  {e.relatedTerms && e.relatedTerms.length > 0 && (
                    <Wrap spacing={1}>
                      {e.relatedTerms.map(rt => {
                        const target = POKER_GLOSSARY[rt];
                        if (!target) return null;
                        return (
                          <WrapItem key={rt}>
                            <Badge
                              fontSize="9px" colorScheme="cyan" variant="outline"
                              cursor="pointer"
                              onClick={() => { setSearch(target.term); setCategory('all'); }}
                            >{target.term}</Badge>
                          </WrapItem>
                        );
                      })}
                    </Wrap>
                  )}
                </VStack>
              </PokerCard>
            ))}
          </VStack>
        )}
      </Container>
    </Box>
  );
};

export default GlossaryPage;
